// src/hooks/usePhraseConfig.ts

import { useCallback, useState } from "react";
import { useAccount, useErrorHandler, useToast } from "../context";
import {
  PhraseConfig,
  phraseConfigService,
  UpdatePhraseConfigRequest,
} from "../services/phraseConfig";
import { logger } from "../utils/logger";

export const usePhraseConfig = () => {
  const { account } = useAccount();
  const { showToast } = useToast();
  const { handleError } = useErrorHandler();

  const [phraseConfig, setPhraseConfig] = useState<PhraseConfig>({
    interest: [],
    disinterest: [],
    blocklist: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPhraseConfig = useCallback(async () => {
    if (!account?.company_id) return;

    setLoading(true);
    try {
      const response = await phraseConfigService.getPhraseConfig(
        account.company_id,
      );
      setPhraseConfig({
        interest: response.interest || [],
        disinterest: response.disinterest || [],
        blocklist: response.blocklist || [],
      });
    } catch (error) {
      logger.error("Erro ao carregar configuração de frases:", error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [account?.company_id, handleError]);

  const updatePhraseConfig = useCallback(
    async (config: UpdatePhraseConfigRequest) => {
      if (!account?.company_id) return;

      setSaving(true);
      try {
        await phraseConfigService.updatePhraseConfig(
          account.company_id,
          config,
        );
        setPhraseConfig(config);
        showToast({
          type: "success",
          message: "Configuração de frases atualizada com sucesso!",
        });
      } catch (error) {
        logger.error("Erro ao atualizar configuração de frases:", error);
        handleError(error);
      } finally {
        setSaving(false);
      }
    },
    [account?.company_id, showToast, handleError],
  );

  const addPhrase = useCallback(
    (type: "interest" | "disinterest" | "blocklist", phrase: string) => {
      if (!phrase.trim()) return;

      const trimmedPhrase = phrase.trim().toLowerCase();
      const currentPhrases = phraseConfig[type];

      // Evitar duplicatas
      if (currentPhrases.includes(trimmedPhrase)) {
        showToast({
          type: "warning",
          message: "Esta frase já existe na lista",
        });
        return;
      }

      const newPhrases = [...currentPhrases, trimmedPhrase];
      setPhraseConfig((prev) => ({
        ...prev,
        [type]: newPhrases,
      }));
    },
    [phraseConfig, showToast],
  );

  const removePhrase = useCallback(
    (type: "interest" | "disinterest" | "blocklist", index: number) => {
      const newPhrases = phraseConfig[type].filter((_, i) => i !== index);
      setPhraseConfig((prev) => ({
        ...prev,
        [type]: newPhrases,
      }));
    },
    [phraseConfig],
  );

  const saveChanges = useCallback(async () => {
    await updatePhraseConfig(phraseConfig);
  }, [phraseConfig, updatePhraseConfig]);

  return {
    phraseConfig,
    loading,
    saving,
    loadPhraseConfig,
    updatePhraseConfig,
    addPhrase,
    removePhrase,
    saveChanges,
  };
};
